import * as React from 'react';
import { useQuery } from 'convex/react';
import { api, type Doc, type Id } from '@/convex';
import {
  ApiClientError,
  isTestClerkUserId,
  postJson,
  testClerkUserHeaders,
} from '@/lib/client-http';
import { useClerkUser } from '@/hooks/useClerkUser';
import type {
  ScanResult,
  ScanProgress,
  CodebaseProfile,
} from '@/lib/github-types';

interface ScanArgs {
  orgId: Id<'orgs'>;
  projectId: Id<'projects'>;
  repoFullName: string;
  cacheMode?: 'reuse' | 'force' | 'skip';
  scanProfile?: 'full' | 'onboarding';
  /**
   * Attribution for telemetry + UI affordances. Onboarding wizard passes
   * 'onboarding'; the components page rescan button passes
   * 'manual-rescan'. Defaults to 'user' for any unattributed caller.
   */
  triggeredBy?:
    | 'user'
    | 'onboarding'
    | 'onboarding-followup'
    | 'manual-rescan'
    | 'webhook';
}

type ScanRunStatus = 'queued' | 'running' | 'deferred' | 'succeeded' | 'failed';

type PublicScanRun = Omit<Doc<'scanRuns'>, 'requestedByClerkUserId'>;

// Client-side last-resort stall fallback. The server emits a liveness
// heartbeat every ~30s and its watchdog (scanRuns.STALE_RUN_MS = 6m) authors
// the canonical `failed`/SCAN_STALLED status, which this hook surfaces
// directly. This local threshold sits *above* the server's so the server
// always wins in normal operation; it only fires if the backend watchdog
// itself never ran (e.g. its scheduled mutation was lost).
const STALLED_SCAN_MS = 7 * 60 * 1000;

function isActiveStatus(status: ScanRunStatus | null) {
  return status === 'queued' || status === 'running' || status === 'deferred';
}

function isPotentiallyStalled(scanRun: PublicScanRun, updatedAt: number) {
  if (scanRun.status !== 'queued' && scanRun.status !== 'running') {
    return false;
  }
  return Date.now() - updatedAt > STALLED_SCAN_MS;
}

function scanRunDetail(scanRun: PublicScanRun) {
  return {
    runId: scanRun._id,
    phase: scanRun.phase ?? null,
    updatedAt: scanRun.updatedAt,
    repoFullName: scanRun.repoFullName,
  };
}

function stalledScanMessage(scanRun: PublicScanRun) {
  const phase = scanRun.phase ? ` during ${scanRun.phase}` : '';
  return `Scan stopped reporting progress${phase}. Try again to start a fresh scan.`;
}

function effectiveUpdatedAt(args: {
  scanRun: PublicScanRun;
  requestedRunId: string | null;
  attemptStartedAt: number | null;
}) {
  if (
    args.requestedRunId &&
    args.scanRun._id === args.requestedRunId &&
    args.attemptStartedAt
  ) {
    return Math.max(args.scanRun.updatedAt, args.attemptStartedAt);
  }

  return args.scanRun.updatedAt;
}

export function useRepoScan(args: ScanArgs | null): {
  scanning: boolean;
  status: ScanRunStatus | null;
  scanRun: PublicScanRun | null | undefined;
  progress: ScanProgress;
  results: ScanResult[];
  error: string | null;
  errorCode: string | null;
  errorDetail: unknown;
  jsxPatternCount: number;
  codebaseProfile: CodebaseProfile | null;
  resultsReady: boolean;
  startScan: (nextArgs: ScanArgs) => Promise<void>;
} {
  const [scanning, setScanning] = React.useState(!!args);
  const [activeArgs, setActiveArgs] = React.useState<ScanArgs | null>(args);
  const [requestedRunId, setRequestedRunId] = React.useState<string | null>(
    null,
  );
  const [results, setResults] = React.useState<ScanResult[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [errorCode, setErrorCode] = React.useState<string | null>(null);
  const [errorDetail, setErrorDetail] = React.useState<unknown>(null);
  const [startRequestInFlight, setStartRequestInFlight] = React.useState(false);
  const [attemptStartedAt, setAttemptStartedAt] = React.useState<number | null>(
    null,
  );
  const [jsxPatternCount, setJsxPatternCount] = React.useState(0);
  const [codebaseProfile, setCodebaseProfile] =
    React.useState<CodebaseProfile | null>(null);
  const [progress, setProgress] = React.useState<ScanProgress>({
    filesProcessed: 0,
    totalFiles: 0,
    componentsFound: 0,
  });
  const { user } = useClerkUser();
  const clerkUserId = user?.id ?? null;
  const runIdRef = React.useRef(0);

  React.useEffect(() => {
    setActiveArgs(args);
    if (args) setScanning(true);
  }, [args]);

  const scanRun = useQuery(
    api.scanRuns.getLatestForProject,
    activeArgs
      ? {
          projectId: activeArgs.projectId,
          repoFullName: activeArgs.repoFullName,
          profile: activeArgs.scanProfile ?? 'full',
          ...(isTestClerkUserId(clerkUserId) ? { clerkUserId } : {}),
        }
      : 'skip',
  ) as PublicScanRun | null | undefined;
  const activeBinding = useQuery(
    api.projects.getBindingByRepo,
    activeArgs
      ? {
          projectId: activeArgs.projectId,
          repoFullName: activeArgs.repoFullName,
        }
      : 'skip',
  ) as { _id: Id<'projectRepoBindings'> } | null | undefined;
  const waitingForBinding =
    !!activeArgs &&
    scanRun?.status === 'succeeded' &&
    activeBinding === undefined;
  const shouldLoadRegistry =
    !!activeArgs && scanRun?.status === 'succeeded' && !!activeBinding;
  const registryComponents = useQuery(
    api.componentRegistry.listByProject,
    shouldLoadRegistry && activeBinding
      ? {
          projectId: activeArgs.projectId,
          bindingId: String(activeBinding._id),
          kind: 'component',
          ...(isTestClerkUserId(clerkUserId) ? { clerkUserId } : {}),
        }
      : 'skip',
  ) as
    | Array<{
        componentKey: string;
        displayName: string;
        lastSeenFilePath?: string;
      }>
    | undefined;

  const startScan = React.useCallback(async (nextArgs: ScanArgs) => {
    const runId = runIdRef.current + 1;
    const startedAt = Date.now();
    runIdRef.current = runId;
    setActiveArgs(nextArgs);
    setRequestedRunId(null);
    setAttemptStartedAt(startedAt);
    setStartRequestInFlight(true);
    setScanning(true);
    setError(null);
    setErrorCode(null);
    setErrorDetail(null);
    setResults([]);
    setCodebaseProfile(null);
    setProgress({ filesProcessed: 0, totalFiles: 0, componentsFound: 0 });

    try {
      const queued = await postJson<{
        ok: boolean;
        scanRunId: string;
        status: ScanRunStatus;
      }>(
        '/api/findings/scan',
        {
          orgId: nextArgs.orgId,
          projectId: nextArgs.projectId,
          repoFullName: nextArgs.repoFullName,
          cacheMode: nextArgs.cacheMode ?? 'reuse',
          scanProfile: nextArgs.scanProfile ?? 'full',
          triggeredBy: nextArgs.triggeredBy ?? 'user',
        },
        'Scan failed',
        { headers: testClerkUserHeaders(clerkUserId) },
      );
      if (runIdRef.current === runId) {
        setRequestedRunId(queued.scanRunId);
        setScanning(isActiveStatus(queued.status));
      }
    } catch (err) {
      if (runIdRef.current === runId) {
        if (err instanceof ApiClientError) {
          setErrorCode(err.code);
          setErrorDetail(err.detail);
        }
        setError(err instanceof Error ? err.message : 'Scan failed');
        setAttemptStartedAt(null);
        setScanning(false);
      }
    } finally {
      if (runIdRef.current === runId) {
        setStartRequestInFlight(false);
      }
    }
  }, [clerkUserId]);

  React.useEffect(() => {
    if (!scanRun) return;
    if (startRequestInFlight) return;
    if (requestedRunId && scanRun._id !== requestedRunId) return;
    const freshnessUpdatedAt = effectiveUpdatedAt({
      scanRun,
      requestedRunId,
      attemptStartedAt,
    });

    setProgress({
      phase: scanRun.phase,
      filesProcessed: scanRun.filesProcessed ?? scanRun.fileCount ?? 0,
      totalFiles: scanRun.totalFiles ?? scanRun.fileCount ?? 0,
      componentsFound: scanRun.componentsFound ?? scanRun.componentCount ?? 0,
      tokensFound: scanRun.tokensFound ?? scanRun.tokenCount,
      hardcodedFound: scanRun.hardcodedFound ?? scanRun.hardcodedCount,
      latestComponent: scanRun.latestComponent,
    });

    if (scanRun.status === 'failed') {
      setErrorCode(scanRun.errorCode ?? null);
      setErrorDetail(scanRunDetail(scanRun));
      setError(scanRun.errorMessage ?? 'Scan failed');
      setAttemptStartedAt(null);
      setScanning(false);
      return;
    }

    if (scanRun.status === 'succeeded') {
      setError(null);
      setErrorCode(null);
      setErrorDetail(null);
      setJsxPatternCount(scanRun.jsxPatternCount ?? 0);
      setCodebaseProfile(null);
      setAttemptStartedAt(null);
      setScanning(false);
      return;
    }

    if (isPotentiallyStalled(scanRun, freshnessUpdatedAt)) {
      setErrorCode('SCAN_STALLED');
      setErrorDetail(scanRunDetail(scanRun));
      setError(stalledScanMessage(scanRun));
      setScanning(false);
      return;
    }

    setScanning(true);
    setError(null);
    setErrorDetail(null);
    if (scanRun.status === 'deferred') {
      setErrorCode(scanRun.errorCode ?? null);
      setError(null);
    } else {
      setErrorCode(null);
    }
  }, [attemptStartedAt, requestedRunId, scanRun, startRequestInFlight]);

  React.useEffect(() => {
    if (!scanRun) return;
    if (startRequestInFlight) return;
    if (requestedRunId && scanRun._id !== requestedRunId) return;
    if (scanRun.status !== 'queued' && scanRun.status !== 'running') return;

    const freshnessUpdatedAt = effectiveUpdatedAt({
      scanRun,
      requestedRunId,
      attemptStartedAt,
    });
    const staleIn = freshnessUpdatedAt + STALLED_SCAN_MS - Date.now();
    if (staleIn <= 0) {
      setErrorCode('SCAN_STALLED');
      setErrorDetail(scanRunDetail(scanRun));
      setError(stalledScanMessage(scanRun));
      setScanning(false);
      return;
    }

    const timeout = window.setTimeout(() => {
      setErrorCode('SCAN_STALLED');
      setErrorDetail(scanRunDetail(scanRun));
      setError(stalledScanMessage(scanRun));
      setScanning(false);
    }, staleIn + 250);

    return () => window.clearTimeout(timeout);
  }, [attemptStartedAt, requestedRunId, scanRun, startRequestInFlight]);

  React.useEffect(() => {
    if (!registryComponents) return;
    setResults(
      registryComponents.map((component) => ({
        key: component.componentKey,
        name: component.displayName,
        path: component.lastSeenFilePath ?? '',
        type: 'component' as const,
      })),
    );
  }, [registryComponents]);

  return {
    scanning: scanning || startRequestInFlight,
    status: scanRun?.status ?? (scanning ? 'queued' : null),
    scanRun,
    progress,
    results,
    error,
    errorCode,
    errorDetail,
    jsxPatternCount,
    codebaseProfile,
    resultsReady:
      !waitingForBinding &&
      (!shouldLoadRegistry || registryComponents !== undefined),
    startScan,
  };
}
